import { useState, useEffect, useRef } from "react";
import { Spin, Tag } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper";
import MediaQuery from "react-responsive";

import { GetUrlParams, ObjectToURL } from "scripts/helpers";
import { Modals } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import PWAJobCard from "components/job-card/pwa";
import SPAJobCard from "components/job-card/spa";
import PWAFooter from "components/footer/pwa";
import LoadMore from "components/loadmore";
import Icons from "scripts/icons";

const PWAHeader = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const inputRef = useRef();
	const { category, coordinates, sortBy, search } = GetUrlParams(
		location.search
	);

	const handleSearch = debounce((e) => {
		const query = ObjectToURL({
			category,
			coordinates,
			sortBy,
			search: e.target.value,
		});

		navigate({ pathname: "/jobs", search: `?${query}` });
	}, 1000);

	const componentDidUpdate = () => {
		inputRef.current.value = search || "";
	};

	useEffect(componentDidUpdate, [location.search]);

	return (
		<div className="pwa-header-search">
			<div className="container">
				<input
					type="search"
					className="search"
					placeholder={`${Resources.search}`}
					onChange={handleSearch}
					ref={inputRef}
				/>

				<Link
					to={`${location.search}#${Modals.LOCATION}`}
					className="location"
				>
					<Icons.Location />
				</Link>

				<Link
					to={`${location.search}#${Modals.CATEGORY}`}
					className="filter"
				>
					<Icons.Category />
				</Link>

				<Link
					to={`${location.search}#${Modals.AROUND}`}
					className="around"
				>
					<Icons.Discover />
				</Link>

				<Link
					to={`${location.search}#${Modals.SORT}`}
					className="sort"
				>
					<Icons.Sort />
				</Link>
			</div>
		</div>
	);
};

const Component = ({ categories, cities, specialJobsQuantity }) => {
	const initialState = { banners: [], jobs: [] };
	const [state, setState] = useState(initialState);
	const { banners, jobs, loaded, loading, loadMore, page } = state;
	const location = useLocation();
	const navigate = useNavigate();
	const { category, coordinates, search, sortBy } = GetUrlParams(
		location.search
	);
	const categoryTitle = category
		? categories.find((o) => o.id === parseInt(category))?.title
		: null;
	const inputRef = useRef();

	const handleTag = (e, type) => {
		e.preventDefault();

		switch (type) {
			case "city":
				navigate({
					pathname: location.pathname,
					search: location.search,
					hash: Modals.LOCATION,
				});
				break;
			case "category":
				const categoryQuery = ObjectToURL({ coordinates, search, sortBy });

				navigate({
					pathname: location.pathname,
					search: `?${categoryQuery}`,
				});
				break;
			case "search":
				const searchQuery = ObjectToURL({ category, coordinates, sortBy });

				navigate({
					pathname: location.pathname,
					search: `?${searchQuery}`,
				});
				break;
			case "sortBy":
				const SortByQuery = ObjectToURL({ category, coordinates, search });

				navigate({
					pathname: location.pathname,
					search: `?${SortQuery}`,
				});
				break;
			case "coordinates":
				const coordinatesQuery = ObjectToURL({ category, search, sortBy });

				navigate({
					pathname: location.pathname,
					search: `?${coordinatesQuery}`,
				});
				break;
			default:
				break;
		}
	};

	const handleBanners = () => {
		setState((prevState) => ({
			...prevState,
			loaded: false,
		}));

		API.banner
			.list({
				categoryId: category || "null",
				cities: cities.map((o) => o.id).join(","),
				paginate: false,
			})
			.then(({ data: banners }) => {
				setState((prevState) => ({
					...prevState,
					loaded: true,
					banners,
				}));
			})
			.catch();
	};

	const handleJobs = (page) => {
		if (!loading) {
			setState((prevState) => ({
				...prevState,
				loading: true,
			}));

			API.job
				.list({
					categoryId: category,
					coordinates,
					cities: cities.map((o) => o.id).join(","),
					sortBy,
					page,
					pageSize: specialJobsQuantity,
					search,
				})
				.then(({ data, meta: { currentPage: page, perPage, total } }) => {
					setState((prevState) => ({
						...prevState,
						loaded: true,
						loading: false,
						loadMore: page * perPage < total,
						jobs: [...prevState.jobs, ...data],
						page,
					}));
				})
				.catch(() => {
					setState((prevState) => ({ ...prevState, loading: false }));
				});
		}
	};

	const handleLoadmore = () => {
		handleJobs(page + 1);
	};

	const componentDidUpdate = () => {
		setState((prevState) => ({ ...prevState, jobs: [] }));

		handleJobs(1);
	};

	const componentDidMount = () => {
		document.title = Resources.jobs;
	};

	useEffect(handleBanners, [category]);
	useEffect(componentDidUpdate, [category, search, coordinates, sortBy]);
	useEffect(componentDidMount, []);

	return loaded ? (
		<>
			<MediaQuery maxWidth={992}>
				<PWAHeader />
			</MediaQuery>
			<div className="page-search">
				<div className="filters">
					<div className="container">
						<div className="tags">
							{!!cities.length && (
								<Tag
									closable
									closeIcon={<Icons.CloseCross />}
									onClose={(e) => handleTag(e, "city")}
								>
									<Icons.Location />

									{cities.map((o) => o.name).join(`${Resources.comma} `)}
								</Tag>
							)}

							{!!search && (
								<Tag
									closable
									closeIcon={<Icons.CloseCross />}
									onClose={(e) => handleTag(e, "search")}
								>
									<Icons.Search />

									{search}
								</Tag>
							)}

							{!!category && (
								<Tag
									closable
									closeIcon={<Icons.CloseCross />}
									onClose={(e) => handleTag(e, "category")}
								>
									<Icons.Category />

									{categoryTitle}
								</Tag>
							)}

							{!!sortBy && (
								<Tag
									closable
									closeIcon={<Icons.CloseCross />}
									onClose={(e) => handleTag(e, "sortBy")}
								>
									<Icons.Sort />

									{sortBy === "discount"
										? `${Resources.sort} ${Resources.on} ${Resources.basis} ${Resources.discount} `
										: `${Resources.sort} ${Resources.on} ${Resources.basis} ${Resources.score} `}
								</Tag>
							)}
							{!!coordinates && (
								<Tag
									closable
									closeIcon={<Icons.CloseCross />}
									onClose={(e) => handleTag(e, "coordinates")}
								>
									<Icons.Discover />
									{Resources.around} {Resources.your}
								</Tag>
							)}
						</div>
					</div>
				</div>
				{!!banners.length && (
					<div className="banners">
						<div className="container">
							<Swiper
								autoplay={{ delay: 3000 }}
								pagination={{ el: ".swiper-pagination", clickable: true }}
								breakpoints={{
									992: {
										slidesPerView: 2,
										spaceBetween: 24,
									},
								}}
								resistanceRatio={0}
								modules={[Autoplay, Pagination]}
							>
								{banners.map((o) => (
									<SwiperSlide key={o.id}>
										<Link to="#">
											<img
												src={o.cover}
												alt={o.title}
											/>
										</Link>
									</SwiperSlide>
								))}
							</Swiper>
							<div className="swiper-pagination"></div>
						</div>
					</div>
				)}
				<div className="jobs">
					<div className="container">
						<p className="heading">
							{Resources.jobs} {Resources.special}
						</p>
						<div className="list">
							<ul className="row">
								{jobs?.map((o) => (
									<li
										key={o.id}
										className="col-12 col-md-6 col-lg-4 col-xl-3"
									>
										<MediaQuery maxWidth={992}>
											<PWAJobCard {...o} />
										</MediaQuery>
										<MediaQuery minWidth={992}>
											<SPAJobCard {...o} />
										</MediaQuery>
									</li>
								))}
							</ul>
						</div>
						{loadMore && (
							<LoadMore
								onScreen={handleLoadmore}
								loading={loading}
							/>
						)}
					</div>
				</div>
			</div>
			<MediaQuery maxWidth={992}>
				<PWAFooter active="jobs" />
			</MediaQuery>
		</>
	) : (
		<Spin className="page-loading" />
	);
};

export default connect((state) => {
	return {
		categories: state.categories,
		cities: state.configs?.cities || [],
		specialJobsQuantity: state.settings.specialJobsQuantity,
	};
})(Component);
